/**
 * LowBalanceBanner
 *
 * Global top banner shown whenever the clinic's notification credit wallet
 * is low or empty (gateState !== "sufficient"). Mirrors
 * SubscriptionHealthBanner's placement (full-width, sidebar-offset) and
 * per-session dismiss convention. Mounted once in App.tsx next to
 * GlobalBillingBanners so it's visible on every page, not just Settings.
 */
import React, { useState } from "react";

import { useSidebar } from "../../contexts/SidebarContext";
import { useWalletBalance, WalletGateState } from "../../hooks/useWalletBalance";
import { BuyCreditsModal } from "./BuyCreditsModal";

const DISMISS_KEYS: Record<Exclude<WalletGateState, "sufficient">, string> = {
  low:  "wallet_low_balance_dismissed",
  zero: "wallet_zero_balance_dismissed",
};

export const LowBalanceBanner: React.FC = () => {
  const { gateState, balancePaise, loading, refetch } = useWalletBalance();
  const { isExpanded } = useSidebar();
  const [showBuyCredits, setShowBuyCredits] = useState(false);
  const [dismissedVariant, setDismissedVariant] = useState<string | null>(null);

  if (loading || balancePaise === null) return null;
  if (gateState === "sufficient") return null;
  if (sessionStorage.getItem(DISMISS_KEYS[gateState]) || dismissedVariant === gateState) return null;

  const isZero = gateState === "zero";
  const sidebarOffset = isExpanded ? 240 : 72;

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEYS[gateState], "1");
    setDismissedVariant(gateState);
  };

  const message = isZero
    ? "You're out of notification credits — WhatsApp, SMS & email sends are blocked until you top up."
    : "Notification credits are running low — top up soon to avoid interrupted sends.";

  return (
    <>
      <div className="w-full" style={{ paddingLeft: sidebarOffset }}>
        <div
          className={`w-full flex items-center justify-between px-4 sm:px-6 py-2.5 gap-4 ${
            isZero ? "bg-red-600" : "bg-amber-50 border-b border-amber-200"
          }`}
        >
          <div className="flex items-center gap-2 min-w-0">
            <svg
              className={`w-4 h-4 flex-shrink-0 ${isZero ? "text-white" : "text-amber-600"}`}
              viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span className={`text-sm font-medium line-clamp-2 ${isZero ? "text-white" : "text-amber-800"}`}>
              {message}
            </span>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => setShowBuyCredits(true)}
              className={`text-xs font-semibold px-3 py-2 min-h-[36px] inline-flex items-center rounded-lg transition-colors ${
                isZero ? "bg-white text-red-700 hover:bg-red-50" : "bg-amber-700 text-white hover:bg-amber-800"
              }`}
            >
              Buy credits
            </button>
            <button
              onClick={dismiss}
              aria-label="Dismiss"
              className={`p-2.5 rounded-md hover:bg-black/10 ${isZero ? "text-white" : "text-amber-700"}`}
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {showBuyCredits && (
        <BuyCreditsModal onClose={() => setShowBuyCredits(false)} onPurchased={refetch} />
      )}
    </>
  );
};

export default LowBalanceBanner;
